import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, InputNumber, Modal, Popconfirm, Row, Space, Table, Tag, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined, SaveOutlined, SyncOutlined, CheckOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  applyRankMappings,
  createRankMapping,
  deleteRankMapping,
  fetchRankMappingSettings,
  fetchRankMappings,
  syncRankMappings,
  updateRankMappingSettings
} from '../actions';

const { Text } = Typography;

const RankMappingSettings = () => {
  const dispatch = useDispatch();
  const { settings, mappings, loading, pagination } = useSelector(
    state => state.categoryManagement?.rankMapping || {}
  );
  const [form] = Form.useForm();
  const [addForm] = Form.useForm();
  const [search, setSearch] = useState('');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchRankMappingSettings());
    dispatch(fetchRankMappings());
  }, [dispatch]);

  useEffect(() => {
    form.setFieldsValue({
      sheetId: settings?.sheetId || '',
      sheetName: settings?.sheetName || 'RankMapping'
    });
  }, [form, settings]);

  const handleSaveSettings = async () => {
    const values = await form.validateFields();
    const result = await dispatch(updateRankMappingSettings(values));
    if (result.success) {
      message.success('Đã lưu cấu hình Rank Mapping');
    } else {
      message.error(result.error || 'Không thể lưu cấu hình');
    }
  };

  const handleSync = async () => {
    const values = await form.validateFields();
    const result = await dispatch(syncRankMappings(values));
    if (result.success) {
      message.success('Đã đưa job đồng bộ Rank Mapping vào queue');
    } else {
      message.error(result.error || 'Không thể đồng bộ Rank Mapping');
    }
  };

  const handleApply = async () => {
    const result = await dispatch(applyRankMappings());
    if (result.success) {
      message.success(`Đã apply rank quy chuẩn cho ${result.data?.updatedCount || 0} SKU`);
      dispatch(fetchRankMappings({ page: pagination?.current || 1, pageSize: pagination?.pageSize || 50, search }));
    } else {
      message.error(result.error || 'Không thể apply Rank Mapping');
    }
  };

  const handleTableChange = (nextPagination) => {
    dispatch(fetchRankMappings({
      page: nextPagination.current,
      pageSize: nextPagination.pageSize,
      search
    }));
  };

  const handleAddManual = async () => {
    try {
      const values = await addForm.validateFields();
      setAddLoading(true);
      const result = await dispatch(createRankMapping(values));
      if (result.success) {
        message.success('Đã thêm mapping mới');
        setAddModalVisible(false);
        addForm.resetFields();
        dispatch(fetchRankMappings({ page: 1, pageSize: pagination?.pageSize || 50, search }));
      } else {
        message.error(result.error || 'Không thể thêm mapping');
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (record) => {
    const result = await dispatch(deleteRankMapping(record.id));
    if (result.success) {
      message.success('Đã xóa mapping');
      dispatch(fetchRankMappings({ page: pagination?.current || 1, pageSize: pagination?.pageSize || 50, search }));
    } else {
      message.error(result.error || 'Không thể xóa mapping');
    }
  };

  const columns = [
    {
      title: 'Amazon Category / Rank Name',
      dataIndex: 'amazonCategoryName',
      key: 'amazonCategoryName',
      ellipsis: true
    },
    {
      title: 'Normalized Rank Coefficient',
      dataIndex: 'normalizedRank',
      key: 'normalizedRank',
      width: 220,
      align: 'right',
      render: value => value !== null && value !== undefined ? Number(value).toLocaleString(undefined, { maximumFractionDigits: 6 }) : '-'
    },
    {
      title: 'Sheet Row',
      dataIndex: 'sheetRowNumber',
      key: 'sheetRowNumber',
      width: 100,
      align: 'center'
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: active => active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>
    },
    {
      title: 'Synced At',
      dataIndex: 'syncedAt',
      key: 'syncedAt',
      width: 180,
      render: value => value ? new Date(value).toLocaleString() : '-'
    },
    {
      title: '',
      key: 'action',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title="Xóa mapping này?"
          onConfirm={() => handleDelete(record)}
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      )
    }
  ];

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={14}>
              <Form.Item
                label="Google Sheet ID / URL"
                name="sheetId"
                rules={[{ required: true, message: 'Nhập Google Sheet ID hoặc URL' }]}
              >
                <Input placeholder="Google Sheet ID hoặc URL" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Sheet tab" name="sheetName">
                <Input placeholder="RankMapping" />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label=" ">
                <Space>
                  <Button icon={<SaveOutlined />} onClick={handleSaveSettings} loading={loading}>
                    Save
                  </Button>
                  <Button type="primary" icon={<SyncOutlined />} onClick={handleSync} loading={loading}>
                    Sync
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Space size={8} wrap>
          <Text type="secondary">Last sync:</Text>
          <Tag color={settings?.lastSyncStatus === 'success' ? 'green' : settings?.lastSyncStatus === 'failed' ? 'red' : 'blue'}>
            {settings?.lastSyncStatus || 'not synced'}
          </Tag>
          <Text type="secondary">{settings?.lastSyncAt || '-'}</Text>
          {settings?.lastSyncMessage && <Text type="secondary">{settings.lastSyncMessage}</Text>}
        </Space>
      </Card>

      <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
        <Col>
          <Space>
            <Input.Search
              allowClear
              placeholder="Search Amazon category"
              value={search}
              onChange={event => setSearch(event.target.value)}
              onSearch={value => dispatch(fetchRankMappings({ page: 1, pageSize: pagination?.pageSize || 50, search: value }))}
              style={{ width: 300 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => dispatch(fetchRankMappings({ page: pagination?.current || 1, pageSize: pagination?.pageSize || 50, search }))}
              loading={loading}
            >
              Reload
            </Button>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
              Add Manual
            </Button>
            <Button icon={<CheckOutlined />} onClick={handleApply} loading={loading}>
              Apply to SKU
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={mappings || []}
        loading={loading}
        size="small"
        pagination={{
          current: pagination?.current || 1,
          pageSize: pagination?.pageSize || 50,
          total: pagination?.total || 0,
          showSizeChanger: true,
          showTotal: total => `Total ${total} mappings`
        }}
        onChange={handleTableChange}
      />

      <Modal
        title="Add Manual Rank Mapping"
        open={addModalVisible}
        onOk={handleAddManual}
        onCancel={() => { setAddModalVisible(false); addForm.resetFields(); }}
        okText="Add"
        confirmLoading={addLoading}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            label="Amazon Category Name"
            name="amazonCategoryName"
            rules={[{ required: true, message: 'Nhập tên Amazon category' }]}
          >
            <Input placeholder="VD: Clothing, Shoes & Jewelry" />
          </Form.Item>
          <Form.Item
            label="Normalized Rank Coefficient"
            name="normalizedRank"
            tooltip="Rank quy chuẩn = Main Rank x hệ số này"
            rules={[{ required: true, message: 'Nhập hệ số rank quy chuẩn' }, { type: 'number', min: 0.000001, message: 'Hệ số phải là số dương' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0.000001}
              step={0.01}
              precision={6}
              placeholder="VD: 0.25"
              parser={value => value?.replace(/,/g, '')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RankMappingSettings;
